import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { CustomBadge } from "src/ui/shared/CustomBadge";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "src/ui/shared/CustomButton";
import { TextareaField } from "src/ui/shared/form/TextareaField";
import { TextField } from "src/ui/shared/form/TextField";
import { LoadingIcon } from "src/ui/shared/Loading";
import { Modal } from "src/ui/shared/Modal/Modal";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { addProductVariable, deleteProductVariable, fetchProductVariables } from "@dashboard/products/api/index";
import { ProductVariable } from "@dashboard/products/types/index";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Chip, Paper, TableBody, TableContainer, TableRow, Typography } from "@mui/material";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme) => ({
    addButtonBox: { display: "flex", justifyContent: "flex-end", marginBottom: theme.spacing(3) },
    loadingBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
}));

const maxSize = 10240;
const validationSchema = z.object({
    key: z.string().regex(/^[A-Za-z][A-Za-z0-9_-]*$/),
    value: z.string().max(maxSize, i18n.t("common:validation.maxFileSize")),
    description: z.string().optional(),
});

type Props = {
    productId: number;
};

type Secret = Omit<ProductVariable, "id">;
type FormValues = Secret;

export const VariableSection = ({ productId }: Props) => {
    const [productVariables, setProductVariables] = React.useState<Secret[]>([]);
    const [open, setOpen] = React.useState(false);
    const [deletingKey, setDeletingKey] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common"]);
    const validate = useValidationSchema(validationSchema);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const handleAddVariable = async (payload: Secret, form: FormApi<FormValues>) => {
        try {
            await addProductVariable({
                productId,
                variableData: payload,
            });
            setProductVariables([
                ...productVariables,
                { key: payload.key, value: payload.value, description: payload.description },
            ]);
            showSuccessToast(t("pages:productPage.toast.successfullyAddedProductVariable"));
            form.restart();
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message || t("pages:productPage.toast.failedToAddVariable"));
        }
    };

    const handleDeleteVariable = async (keyToDelete: string) => {
        setDeletingKey(keyToDelete);
        try {
            await deleteProductVariable(productId, keyToDelete);
            setProductVariables(productVariables.filter((variable) => variable.key !== keyToDelete));
            showSuccessToast(t("pages:productPage.toast.successfullyDeletedProductVariable"));
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message || t("pages:productPage.toast.failedToDeleteVariable"));
        } finally {
            setDeletingKey(null);
        }
    };

    React.useEffect(() => {
        const fetchVariables = async () => {
            try {
                setIsLoading(true);
                const data = await fetchProductVariables(productId);
                setProductVariables(data);
            } catch (error) {
                const err = error as Error;
                showErrorToast(err.message);
                setProductVariables([]);
            } finally {
                setIsLoading(false);
            }
        };

        void fetchVariables();
    }, [productId]);

    return (
        <React.Fragment>
            <CustomBadge badgeContent={productVariables.length}>
                <Chip
                    label={t("pages:productPage.variables")}
                    onClick={() => setOpen(true)}
                    icon={<i className="fa-solid fa-plus" />}
                    disabled={cannotManageProducts}
                />
            </CustomBadge>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={t("modals:productVariable.title")}
                description={t("modals:productVariable.description")}
            >
                <Form<FormValues>
                    validate={validate}
                    onSubmit={handleAddVariable}
                    render={({ handleSubmit, submitting, form }) => (
                        <form onSubmit={handleSubmit} className={classes.form}>
                            <TextField
                                name="key"
                                label={t("common:label.key")}
                                placeholder={t("common:label.key")}
                                variant="outlined"
                                fullWidth
                            />

                            <TextareaField
                                name="value"
                                label={t("common:label.value")}
                                id="value"
                                placeholder={t("common:label.value")}
                                minRows={3}
                                maxRows={5}
                            />

                            <TextField
                                name="description"
                                label={t("common:label.description")}
                                placeholder={t("common:label.description")}
                                id="description"
                            />

                            <div className={classes.addButtonBox}>
                                <PrimaryOutlinedButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                    {t("common:button.add")}
                                </PrimaryOutlinedButton>
                            </div>

                            <TableContainer component={Paper}>
                                <MinimalTable>
                                    <MinimalTableHeader>
                                        <MinimalTableHeaderCell>
                                            <Typography fontWeight={600}>
                                                {t("common:label.key")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell>
                                            <Typography fontWeight={600}>
                                                {t("common:label.description")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableHeader>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <MinimalTableCell colSpan={3}>
                                                    <div className={classes.loadingBox}>
                                                        <LoadingIcon />
                                                    </div>
                                                </MinimalTableCell>
                                            </TableRow>
                                        ) : productVariables.length === 0 ? (
                                            <TableRow>
                                                <MinimalTableCell colSpan={3} align="center">
                                                    <Typography>{t("modals:productVariable.table.noItemsAvailable")}</Typography>
                                                </MinimalTableCell>
                                            </TableRow>
                                        ) : (
                                            productVariables.map((variable) => (
                                                <TableRow key={variable.key}>
                                                    <MinimalTableCell>
                                                        <Typography>{variable.key}</Typography>
                                                    </MinimalTableCell>
                                                    <MinimalTableCell>
                                                        <Typography>{variable.description || "-"}</Typography>
                                                    </MinimalTableCell>
                                                    <MinimalTableCell align="right">
                                                        <DangerOutlinedButton
                                                            onClick={() => handleDeleteVariable(variable.key)}
                                                            loading={deletingKey === variable.key}
                                                        >
                                                            <i className="fas fa-trash-alt" />
                                                        </DangerOutlinedButton>
                                                    </MinimalTableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </MinimalTable>
                            </TableContainer>
                        </form>
                    )}
                />
            </Modal>
        </React.Fragment>
    );
};
